import { createContext, useContext, ReactNode } from "react";
import type { DataAdapter } from "./adapter";

const DataAdapterContext = createContext<DataAdapter | null>(null);

interface DataAdapterProviderProps {
  adapter: DataAdapter;
  children: ReactNode;
}

/**
 * Provider that makes the data adapter available to all dashboard components.
 * The adapter can be either the mock implementation or a real API implementation.
 */
export function DataAdapterProvider({
  adapter,
  children,
}: DataAdapterProviderProps) {
  return (
    <DataAdapterContext.Provider value={adapter}>
      {children}
    </DataAdapterContext.Provider>
  );
}

/**
 * Hook to access the data adapter from any component.
 * Must be used within a DataAdapterProvider.
 */
export function useDataAdapter(): DataAdapter {
  const adapter = useContext(DataAdapterContext);
  if (!adapter) {
    throw new Error("useDataAdapter must be used within a DataAdapterProvider");
  }
  return adapter;
}

// Export the context itself for advanced use cases
export { DataAdapterContext };
